import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, ArrowRight, Phone, Mail, MessageSquare } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login, otpRequest, otpVerify, t, lang, toggleLang } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("email"); // email | phone
  const Arrow = lang === "ar" ? ArrowRight : ArrowLeft;

  // email
  const [email, setEmail] = useState("admin@zayd.sa");
  const [password, setPassword] = useState("admin1234");

  // phone OTP
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpStage, setOtpStage] = useState("request"); // request | verify
  const [submitting, setSubmitting] = useState(false);

  const onEmailLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(t.welcome);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestOtp = async (e) => {
    e.preventDefault();
    if (!phone.trim() || phone.replace(/\D/g, "").length < 8) {
      toast.error(lang === "ar" ? "رقم جوال غير صالح" : "Invalid phone");
      return;
    }
    setSubmitting(true);
    try {
      const res = await otpRequest(phone);
      toast.success(t.codeSent, { description: res?.message || t.otpHint });
      setOtpStage("verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error(lang === "ar" ? "رمز غير صالح" : "Invalid code");
      return;
    }
    setSubmitting(true);
    try {
      const u = await otpVerify(phone, code);
      toast.success(t.welcome);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
      <div className="absolute inset-y-0 end-0 hidden lg:block w-1/2 bg-primary">
        <div className="hero-noise absolute inset-0 opacity-80" />
        <div className="relative h-full flex flex-col justify-end p-12 text-primary-foreground">
          <h2 className="text-4xl font-bold leading-tight">{t.brand}</h2>
          <p className="mt-3 text-white/80 max-w-md">{t.heroSub}</p>
        </div>
      </div>

      <div className="relative w-full max-w-md bg-surface border border-border rounded-2xl p-8 shadow-floating z-10 lg:me-auto lg:ms-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <Arrow className="h-4 w-4" />
          {t.backToStorefront}
        </Link>
        <div className="flex items-center gap-2 mb-2">
          <BrandLogo size={36} />
          <span className="text-lg font-bold">{t.brand}</span>
          <button
            onClick={toggleLang}
            className="ms-auto text-xs text-muted-foreground hover:text-foreground"
            data-testid="login-language-switcher"
          >
            {t.language}
          </button>
        </div>
        <h1 className="text-2xl font-bold mb-1">{t.login}</h1>

        {/* Tabs */}
        <div className="mt-4 inline-flex bg-muted rounded-lg p-1 w-full">
          <button
            onClick={() => setTab("email")}
            data-testid="auth-tab-email"
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === "email" ? "bg-surface shadow-sm" : "text-muted-foreground"
            }`}
          >
            <Mail className="h-4 w-4" />
            {t.tabEmail}
          </button>
          <button
            onClick={() => {
              setTab("phone");
              setOtpStage("request");
            }}
            data-testid="auth-tab-phone"
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === "phone" ? "bg-surface shadow-sm" : "text-muted-foreground"
            }`}
          >
            <Phone className="h-4 w-4" />
            {t.tabPhone}
          </button>
        </div>

        {tab === "email" ? (
          <form onSubmit={onEmailLogin} className="space-y-4 mt-5" data-testid="login-form">
            <p className="text-xs text-muted-foreground">{t.loginHint}</p>
            <div>
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full gap-2" size="lg" disabled={submitting} data-testid="login-submit">
              <Lock className="h-4 w-4" />
              {submitting ? "..." : t.loginCta}
            </Button>
            <p className="text-sm text-muted-foreground text-center mt-2">
              {lang === "ar" ? "ليس لديك حساب؟" : "Don't have an account?"}{" "}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">
                {lang === "ar" ? "أنشئ حساباً" : "Sign up"}
              </Link>
            </p>
          </form>
        ) : (
          <div className="mt-5 space-y-4" data-testid="otp-form">
            <p className="text-xs text-muted-foreground">{t.otpHint}</p>
            {otpStage === "request" ? (
              <form onSubmit={onRequestOtp} className="space-y-4">
                <div>
                  <Label htmlFor="phone">{t.phoneLabel}</Label>
                  <div className="flex gap-2">
                    <span className="inline-flex items-center px-3 rounded-md border border-border bg-muted text-sm font-mono">
                      +966
                    </span>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="5XXXXXXXX"
                      data-testid="otp-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                  size="lg"
                  disabled={submitting}
                  data-testid="otp-send-button"
                >
                  <MessageSquare className="h-4 w-4" />
                  {submitting ? "..." : t.sendCode}
                </Button>
              </form>
            ) : (
              <form onSubmit={onVerifyOtp} className="space-y-4">
                <div>
                  <Label htmlFor="code">{t.enterCode}</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    data-testid="otp-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    required
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    {lang === "ar" ? `أُرسل إلى +966${phone}` : `Sent to +966${phone}`}
                    {" · "}
                    <button
                      type="button"
                      className="underline hover:text-foreground"
                      onClick={() => setOtpStage("request")}
                      data-testid="otp-change-phone"
                    >
                      {lang === "ar" ? "تغيير" : "Change"}
                    </button>
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                  size="lg"
                  disabled={submitting}
                  data-testid="otp-verify-button"
                >
                  <Lock className="h-4 w-4" />
                  {submitting ? "..." : t.verifyCode}
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
